import { createStore } from '@stencil/store'
import Axios from 'axios'
import { flatten, orderBy, some, throttle } from 'lodash-es'
import { AppSharedPropsStore } from 'src/app'
import { currentCrm } from 'src/crm/state'
import { dayjs } from 'src/lib/dayjs'
import { IPlaylist, IPlaylistActions, IPlaylistCall } from 'src/playlist/types'
import { canAccess, IShareUniversal } from 'src/share/universal'
import { SubscriptionProductPlan } from 'src/subscription/types'
import { Deferred } from 'src/utils/async'
import { copyText } from 'src/utils/clipboard'
import { storeForCrm } from 'src/utils/crm'

import { asyncToast, flashToast } from '../../../app/toast'
import { IRichAttendee } from '../../../attendee/types'
import {
  IBookmark,
  IHighlightReel,
  isActionItem,
  isAnnotation,
  isBookmark,
  isScreenShare,
} from '../../../bookmarks/types'
import {
  ICall,
  IClip,
  ICrmOpportunity,
  ICue,
  ICueSpan,
  IIndividualCue,
  INote,
  INoteClip,
  IQuestionClip,
  ISpeaker,
} from '../../../call/types'
import { compareSeconds } from '../../../call/utils'
import { syncToCrm } from '../../../crm/state'
import { useLogging } from '../../../utils/logging'
import { useStoredValue } from '../../../utils/storage'
import { stringToRegex } from '../../../utils/text'
import { AnnotationStore } from './annotation'
import { IAskFathomMessage } from './ask-fathom/types'
import { ICallActionItemAssignment, ICallActionItemDetection, ICallAiNotes, ICallFollowUpEmail } from './types'

declare global {
  interface LocalStorageKeys {
    callVideoSpeed: number
    callVideoMuted: boolean
  }
}

export type TranscriptCue = ICue & { note?: INote }

export const AddAnnotationsAccessTypes = ['host', 'teammate', 'audience_standard', 'audience_admin'] as const
export const InternalTeamLabelAccessTypes = ['host', 'teammate', 'external_view_only'] as const
export const CallAccessTypes = [
  'host',
  'external_view_only', // universal share token, same permissions as audience_limited
  'teammate', // teammate view of /calls/:id page, same permissions as audience_standard
  'audience_limited',
  'audience_standard',
  'audience_admin',
] as const
export type CallAccess = typeof CallAccessTypes[number]

export const hasAddAnnotationsAccess = (access: string): access is typeof AddAnnotationsAccessTypes[number] => {
  return AddAnnotationsAccessTypes.includes(access as typeof AddAnnotationsAccessTypes[number])
}
export const hasInternalTeamLabelAccess = (access: string): access is typeof InternalTeamLabelAccessTypes[number] => {
  return InternalTeamLabelAccessTypes.includes(access as typeof InternalTeamLabelAccessTypes[number])
}

export type CallAttendeeMetadata = {
  attendees?: IRichAttendee[]
  crmOpportunity?: ICrmOpportunity
}

export type CallPlaylistMetadata = {
  currentPlaylistItemSortOrder: number
  playlist: IPlaylist
  nextPlaylistItem: IPlaylistCall
}

export interface IRelatedCall extends Pick<ICall, 'id' | 'title'> {
  recording_started_at: string
  detail_url: string
}

export interface ICallState {
  access: CallAccess
  attendeeMetadata: CallAttendeeMetadata
  relatedCall: IRelatedCall
  bookmarks: IBookmark[]
  call: ICall
  highlightReels: IHighlightReel[]
  notes: INote[]
  chatMessages: {
    id: number
    start_time: number
    sender_name: string
    content: string
  }[]
  questions: {
    clips?: IQuestionClip[]
    duration_seconds?: number
    speakers?: Array<{ name: string; email: string }>
  }
  readonly: boolean
  isAttendeeView: boolean
  actionItemUrl: string
  showCrmSync: boolean
  speakers: ISpeaker[]
  downloadVideoUrl: string
  activateDownloadUrl: string
  showMisattributedTranscriptCuesBanner: boolean
  searchPlaylistsUrl: string
  playlistMetadata: CallPlaylistMetadata
  showSignIn: boolean
  annotationsLockedMessage: string
  actions: Partial<IPageCallDetailActions>
  actionItemDetection: ICallActionItemDetection
  followUpEmail: ICallFollowUpEmail
  actionItemAssignment: ICallActionItemAssignment
  aiNotes: ICallAiNotes
  showSummaryTab: boolean
  showAskFathomTab: boolean
  requiredPlan: SubscriptionProductPlan
}

export interface IPageCallDetailActions {
  googleSignInUrl: string
  microsoftSignInUrl: string
  createCommentUrl: string
  searchMentionablesCommentUrl: string
  premiumPlanUpgradeUrl: string
}

export interface IPlaybackState {
  duration: number
  currentTime: number
  paused: boolean
  speed: number
  muted: boolean
  displayMode: 'normal' | 'theater' | 'fullscreen'
  cueSpans: ICueSpan[]
  seekTime: number
  seekTrigger: string
  seekId: number
  pctElapsed: number
}

export interface ITranscriptState {
  activeCue: TranscriptCue
  scrollCue: TranscriptCue
  autoScroll: boolean
  foundCue: ICue
  searchCues: ICue[]
  searchIndex: number
  searchQuery: string
  transcript: TranscriptCue[][]
  cues: TranscriptCue[]
  copyTranscriptUrl: string
  replaceCuesUrl: string
  trimRecordingUrl: string
  trimmingQueue: IIndividualCue[]
  trimmingActive: boolean
  isTrimming: boolean
  scrollPct: number
}

export interface INotesState {
  bookmarks: IBookmark[]
  highlights: IBookmark[]
  questionSpeakers: Array<{ name: string; email: string }>
  noteClips: INoteClip[]
  totalClipDuration: string
  highlightId: number
  editNote: INote
  actionItemsCompleted: number[]
  noteUrl: string
  isSaving: boolean
  playlistActions: Pick<IPlaylistActions, 'createUrl' | 'indexUrl'>
}

export interface IAskCallState {
  inputValue: string
  messages: IAskFathomMessage[]
  loadingMessages: boolean
  loadingClips: boolean
}

export const CallStore = createStore<ICallState>({
  access: null,
  attendeeMetadata: {} as CallAttendeeMetadata,
  relatedCall: {} as IRelatedCall,
  bookmarks: [],
  call: {} as ICall,
  highlightReels: [],
  notes: [],
  chatMessages: [],
  questions: {},
  readonly: false,
  isAttendeeView: false,
  actionItemUrl: '',
  showCrmSync: false,
  showSignIn: false,
  speakers: [],
  downloadVideoUrl: null,
  activateDownloadUrl: null,
  showMisattributedTranscriptCuesBanner: false,
  searchPlaylistsUrl: null,
  playlistMetadata: {} as CallPlaylistMetadata,
  annotationsLockedMessage: null,
  actions: {},
  actionItemDetection: null,
  followUpEmail: null,
  actionItemAssignment: null,
  aiNotes: null,
  showSummaryTab: false,
  showAskFathomTab: false,
  requiredPlan: null,
})

export const PlaybackStore = createStore<IPlaybackState>({
  duration: 0,
  currentTime: 0,
  paused: true,
  speed: null,
  muted: null,
  displayMode: 'normal' as DisplayMode,
  cueSpans: [],
  seekTime: null,
  seekId: null,
  seekTrigger: null,
  pctElapsed: 0,
})

export const TranscriptStore = createStore<ITranscriptState>({
  autoScroll: true,
  activeCue: null,
  scrollCue: null,
  foundCue: null,
  searchCues: [],
  searchIndex: -1,
  searchQuery: '',
  transcript: [],
  cues: [],
  copyTranscriptUrl: null as string,
  replaceCuesUrl: null as string,
  trimRecordingUrl: null as string,
  scrollPct: 0,
  trimmingActive: false,
  trimmingQueue: [],
  isTrimming: false,
})

export const NotesStore = createStore<INotesState>({
  bookmarks: [],
  highlights: [],
  questionSpeakers: [],
  noteClips: [],
  totalClipDuration: null as string,
  highlightId: null,
  editNote: {} as any,
  actionItemsCompleted: [],
  noteUrl: null as string,
  isSaving: false,
  playlistActions: {},
})

CallStore.onChange('actions', (value) => {
  CallStore.state.showSignIn = !!value?.googleSignInUrl || !!value?.microsoftSignInUrl
})

CallStore.onChange('access', (value) => {
  CallStore.state.readonly = !hasAddAnnotationsAccess(value)
  CallStore.state.isAttendeeView = value === 'external_view_only'
})

CallStore.onChange('questions', (value) => {
  if (value && value.speakers) {
    NotesStore.state.questionSpeakers = value.speakers
  }
})

CallStore.onChange('attendeeMetadata', () => {
  if (storeForCrm(currentCrm())?.state?.refreshingContacts) {
    storeForCrm(currentCrm()).state.refreshingContacts = false
  }
})

PlaybackStore.onChange('paused', () => {
  PlaybackStore.state.seekTime = null
  TranscriptStore.state.autoScroll = true
  AnnotationStore.state.editCue = null
})

PlaybackStore.onChange('seekTime', (value) => {
  // Compare to null so that 0 is allowed
  // Use != rather than !== so that if value is undefined, it is coerced to null
  /* eslint-disable eqeqeq */
  if (value != null) {
    TranscriptStore.state.autoScroll = true
  }
  /* eslint-enable eqeqeq */
})

export type DisplayMode = 'theater' | 'fullscreen' | 'normal'

export function nextDisplayMode() {
  const modes: Record<DisplayMode, DisplayMode> = {
    normal: 'theater',
    theater: 'fullscreen',
    fullscreen: 'normal',
  }
  PlaybackStore.state.displayMode = modes[PlaybackStore.state.displayMode]
}

PlaybackStore.onChange(
  'currentTime',
  throttle((t) => {
    const activeTime = t
    TranscriptStore.state.activeCue = TranscriptStore.state.cues.find((c) => {
      // Find the cue in the transcript such that:
      //  * currentTime (t) >= start_time
      //  * t < next_start_time (if present) or t <= end_time (otherwise)
      // Must use next_start_time for two reasons:
      //  1. Consecutive cues may have a gap between them (e.g., cue1 ends at 11.2 and cue2 starts at 13.5)
      //      in which case auto scroll should not transition until the gap has passed
      //  2. Consecutive cues may not have a gap between them (e.g., cue1 ends at, and cue2 starts at, 12.9)
      //      in which case we consider the second cue to be active (aka treat start-end ranges as open-ended)
      if (compareSeconds(activeTime, c.start_time) === -1) {
        // Short circuit if t < start_time
        return false
      }
      // Continue since t >= start_time
      if (c.next_start_time) {
        // true if t < next_start_time; false otherwise
        return compareSeconds(activeTime, c.next_start_time) === -1
      } else {
        // true if t <= end_time
        return compareSeconds(activeTime, c.end_time) < 1
      }
    })
    PlaybackStore.state.pctElapsed = activeTime / PlaybackStore.state.duration
  }, 250)
)

NotesStore.onChange('noteClips', (clips) => {
  NotesStore.state.highlights = clips.reduce<IBookmark[]>((result, clip) => {
    if (clip.note.bookmark.highlight && result.findIndex((b) => b.id === clip.note.bookmark.id) === -1) {
      result.push(clip.note.bookmark)
    }
    return result
  }, [])
})

TranscriptStore.onChange('searchQuery', (query) => {
  // The following line is needed to force the onChange event for searchIndex so that
  // foundCue will update in the case where searchIndex is (already) 0 and
  // searchCues changes

  const regex = stringToRegex(query, 'i')
  TranscriptStore.state.searchIndex = -1
  TranscriptStore.state.searchCues =
    query.length > 2 ? flatten(TranscriptStore.state.transcript).filter((c) => regex.test(c.text)) : []
  TranscriptStore.state.searchIndex = TranscriptStore.state.searchCues.length ? 0 : -1
})

TranscriptStore.onChange('searchIndex', (value) => {
  TranscriptStore.state.foundCue = TranscriptStore.state.searchCues[value]
})

TranscriptStore.onChange('transcript', (transcript) => {
  TranscriptStore.state.cues = flatten(transcript)
})

export function nextSearchResult() {
  const { searchIndex, searchCues } = TranscriptStore.state
  TranscriptStore.state.searchIndex = (searchIndex + 1) % searchCues.length
}

export function prevSearchResult() {
  const { searchIndex, searchCues } = TranscriptStore.state
  TranscriptStore.state.searchIndex = searchIndex <= 0 ? searchCues.length - 1 : searchIndex - 1
}

export async function editCueText(cue: IIndividualCue, text: string) {
  await Axios.put(TranscriptStore.state.replaceCuesUrl, {
    text: text,
    cue_ids: [cue.id],
  })
}

export async function changeCueSpeaker(cue: IIndividualCue, speaker: ISpeaker) {
  await Axios.put(TranscriptStore.state.replaceCuesUrl, {
    speaker_id: speaker.id,
    cue_ids: [cue.id],
  })
}

let trimDeferred = new Deferred()
let trimTimeout: number

export function isTrimmingCue(cue: IIndividualCue) {
  return some(TranscriptStore.state.trimmingQueue, (i) => i.start_time <= cue.start_time && i.end_time >= cue.end_time)
}

export function trimCue(cue: IIndividualCue) {
  if (TranscriptStore.state.isTrimming) {
    return false
  }
  // If the deferred was completed/rejected then we are starting a new queue and
  // thus need to use a new promise/deferred instance
  if (trimDeferred.isResolved || trimDeferred.isRejected) {
    trimDeferred = new Deferred()
  }
  // Show a toast while we wait for the server call
  if (TranscriptStore.state.trimmingQueue.length === 0) {
    asyncToast(trimDeferred.promise, 'Trimming video…', 'Complete!', 'Error trimming video')
  }
  // Add the current cue to the queue of others that may have been trimmed previously
  TranscriptStore.state.trimmingQueue = [...TranscriptStore.state.trimmingQueue, cue]
  // Debounce the actual server call for the trimming so that multiple clicks
  // can queue up and make a single call
  window.clearTimeout(trimTimeout)
  trimTimeout = window.setTimeout(trimCues, 3000)
  // Get the same promise for each call in the current queue
  return trimDeferred.promise
}

export function endTrimming() {
  TranscriptStore.state.trimmingQueue = []
  TranscriptStore.state.isTrimming = false
}

const trimCues = async () => {
  const queue = TranscriptStore.state.trimmingQueue
  // Reset the queue and disable things while the server executes, make sure to
  // call `endTrimming` when pusher executes
  TranscriptStore.state.isTrimming = true
  try {
    await Axios.put(TranscriptStore.state.trimRecordingUrl, {
      intervals: queue.map((cue) => ({ start_time: cue.start_time, end_time: cue.end_time })),
    })
    trimDeferred.resolve()
  } catch (e) {
    endTrimming()
    trimDeferred.reject(e)
  }
}

CallStore.onChange('notes', () => {
  AnnotationStore.reset()
})

export async function updateNote(note: INote, url: string) {
  if (note.id) {
    window.Sentry?.setContext('note', note)
    AnnotationStore.state.processingNote = note
    NotesStore.state.isSaving = true
    try {
      await Axios.put(`${url}/${note.id}`, note)
      syncToCrm()
    } catch (e) {
      if (e instanceof Error) {
        window.Sentry?.captureException(e)
      }
      flashToast({ type: 'alert', text: 'Error updating highlight, please try again' })
    } finally {
      NotesStore.state.isSaving = false
      AnnotationStore.state.processingNote = null
    }
  }
}

export async function deleteNote(note: INote, url: string) {
  window.Sentry?.setContext('note', note)
  AnnotationStore.state.processingNote = note
  NotesStore.state.isSaving = true
  try {
    flashToast({ type: 'success', text: 'Deleting highlight…' })
    await Axios.delete(`${url}/${note.id}`)
    flashToast({ type: 'success', text: 'Done' })
    syncToCrm()
  } catch (e) {
    if (e instanceof Error) {
      window.Sentry?.captureException(e)
    }
    flashToast({ type: 'alert', text: 'Error deleting highlight, please try again' })
  } finally {
    NotesStore.state.isSaving = false
    AnnotationStore.state.processingNote = null
  }
}

export async function addNote(note: INote, url: string) {
  window.Sentry?.setContext('note', note)
  AnnotationStore.state.processingNote = note
  NotesStore.state.isSaving = true
  note.timestamp = dayjs(CallStore.state.call.recording.started_at).add(note.start_time, 'second').toISOString()
  try {
    await Axios.post(url, note)
    syncToCrm()
  } catch (e) {
    if (e instanceof Error) {
      window.Sentry?.captureException(e)
    }
    flashToast({ type: 'alert', text: 'Error creating highlight, please try again' })
  } finally {
    NotesStore.state.isSaving = false
    AnnotationStore.state.processingNote = null
  }
}

export async function deleteAutoGenerated(url: string) {
  NotesStore.state.isSaving = true
  try {
    flashToast({ type: 'success', text: 'Deleting AI action items…' })
    await Axios.delete(url)
    flashToast({ type: 'success', text: 'Done' })
    syncToCrm()
  } catch (e) {
    if (e instanceof Error) {
      window.Sentry?.captureException(e)
    }
    flashToast({ type: 'alert', text: 'Error deleting action items, please try again' })
  } finally {
    NotesStore.state.isSaving = false
  }
}

CallStore.use(useLogging('Call'))
NotesStore.use(useLogging('Notes', ['highlightId']))
TranscriptStore.use(useLogging('Transcript'))
PlaybackStore.use(useLogging('Playback', ['currentTime', 'pctElapsed']))

PlaybackStore.use(useStoredValue(PlaybackStore, 'callVideoSpeed', 'speed', 1))
PlaybackStore.use(useStoredValue(PlaybackStore, 'callVideoMuted', 'muted', false))

export function isActionItemCompleted(noteId: number) {
  return NotesStore.state.actionItemsCompleted.includes(noteId)
}

export function markActionItem(noteId: number, isDone: boolean) {
  // these next few lines are some hoopla to do an immediate re-render when action items are marked done/not done
  const index = NotesStore.state.actionItemsCompleted.indexOf(noteId)
  if (isDone && index === -1) {
    NotesStore.state.actionItemsCompleted.push(noteId)
  } else if (!isDone && index > -1) {
    NotesStore.state.actionItemsCompleted.splice(index, 1)
  }
  NotesStore.state.actionItemsCompleted = [...NotesStore.state.actionItemsCompleted]

  return Axios.put(CallStore.state.actionItemUrl, { id: noteId, done: isDone }).then(() => syncToCrm())
}

export function markActionItemsDone(noteIds: Array<number>) {
  return Promise.all(noteIds.map((noteId) => markActionItem(noteId, true)))
}

interface IShareUrlActivation {
  access: 'internal_read_only' | 'external_view_only'
  share_url: string
}

export async function getShareableShareUrl(shareable: any) {
  if (!shareable.share_url) {
    // If we don't know the note share url yet, retrieve it
    const activateUrl = shareable.activate_share_url

    await Axios.get<IShareUrlActivation>(activateUrl).then(async (response) => {
      shareable.share_url = response.data.share_url
    })
  }

  return shareable.share_url as string
}

export function getCallShareDisabledReason(universalShareable: IShareUniversal) {
  const shareUnavailable = !canAccess(universalShareable)
  const shareDisabledByTeamAdmin =
    AppSharedPropsStore.state.currentUser?.team?.preferences?.share_links_enabled === false

  return shareDisabledByTeamAdmin
    ? 'Disabled by Team Administrator'
    : shareUnavailable
    ? 'Sharing is not available for this call'
    : null
}

export function clipIsProcessing(clip: IClip) {
  return clip.end_time > PlaybackStore.state.duration && callIsProcessing()
}

function callIsProcessing() {
  return !['finalized'].includes(CallStore.state.call.state)
}

export function getAnnotations() {
  return NotesStore.state.noteClips.filter((noteClip) => isAnnotation(noteClip.note.bookmark))
}

export function getActionItemClips() {
  return orderBy(
    NotesStore.state.noteClips.filter((noteClip) => isActionItem(noteClip.note.bookmark)),
    (noteClip) => noteClip.note.auto_generated // generated should show up second to user created
  )
}

export function getBookmarkClips() {
  return NotesStore.state.noteClips.filter((noteClip) => isBookmark(noteClip.note.bookmark))
}

export function getQuestionClips() {
  return CallStore.state.questions?.clips || []
}

export function getScreenShareClips() {
  return NotesStore.state.noteClips.filter((noteClip) => isScreenShare(noteClip.note.bookmark))
}

export function getNoteClipShareDisabledReason(clip: INoteClip) {
  const shareUnavailable = !(clip?.note?.actions?.activate_share_url || clip?.note?.share_url)
  const shareDisabledByTeamAdmin =
    AppSharedPropsStore.state.currentUser?.team?.preferences?.share_links_enabled === false

  return shareDisabledByTeamAdmin
    ? 'Disabled by Team Administrator'
    : shareUnavailable
    ? 'Sharing is not available for this clip'
    : null
}

export async function activateShareUrl(clip: INoteClip) {
  await Axios.get<{ share_url: string }>(clip.note.actions.activate_share_url).then(async ({ data }) => {
    clip.note.share_url = data.share_url
  })
  return clip.note.share_url
}

export function copyShareUrl(clip: INoteClip) {
  if (clip.note.share_url) {
    const copyPromise = copyText(clip.note.share_url)
    asyncToast(copyPromise, 'Copying…', 'Copied', 'Error copying')
    return copyPromise
  } else {
    const activatePromise = activateShareUrl(clip).then((shareUrl) => ({ plain_text: shareUrl }))
    const copyPromise = copyText(activatePromise, true)
    asyncToast(copyPromise, 'Copying…', 'Copied', 'Error generating share url')
    return copyPromise
  }
}

export function downloadFullVideo() {
  const { downloadVideoUrl, activateDownloadUrl } = CallStore.state
  if (downloadVideoUrl) {
    const u = new URL(downloadVideoUrl)
    if (u.protocol.startsWith('http')) {
      location.assign(u.href)
    }
    return Promise.resolve()
  } else if (activateDownloadUrl) {
    // Request mp4 creation
    const activatePromise = Axios.get(activateDownloadUrl)
    // Wait for pusher to update note clip with download url
    const updatedPromise = new Promise<void>((resolve) => {
      const removeListener = CallStore.onChange('downloadVideoUrl', (url) => {
        if (url) {
          const u = new URL(url)
          if (u.protocol.startsWith('http')) {
            location.assign(u.href)
          }
          resolve()
          removeListener()
        }
      })
    })
    const toastPromise = Promise.all([activatePromise, updatedPromise])
    // Show an async toast while waiting
    asyncToast(toastPromise, 'Preparing download…', 'Download started', 'Error generating file!', 'info-warn')
    return toastPromise
  }
}

export function downloadVideo(clip: INoteClip) {
  if (clip.note.actions.download_url) {
    const u = new URL(clip.note.actions.download_url)
    if (u.protocol.startsWith('http')) {
      location.assign(u.href)
    }
    return Promise.resolve()
  } else {
    // Request mp4 creation
    const activatePromise = Axios.get(clip.note.actions.activate_download_url)
    // Wait for pusher to update note clip with download url
    const updatedPromise = new Promise<void>((resolve) => {
      const removeListener = NotesStore.onChange('noteClips', (clips) => {
        const updatedClip = clips.find((c) => c.id === clip.id)
        if (updatedClip.note.actions.download_url) {
          const u = new URL(updatedClip.note.actions.download_url)
          if (u.protocol.startsWith('http')) {
            location.assign(u.href)
          }
          resolve()
          removeListener()
        }
      })
    })
    const toastPromise = Promise.all([activatePromise, updatedPromise])
    // Show an async toast while waiting
    asyncToast(toastPromise, 'Preparing download…', 'Download started', 'Error generating file!', 'info-warn')
    return toastPromise
  }
}
